// User Feedback — aggregated feedback from retailer / customer / supplier portals.
//
// Backend (live 2026-05-22 via portal_feedback migration 0022 + module):
//   GET   /api/admin/feedback?portal=&status=&from=YYYY-MM-DD&to=YYYY-MM-DD
//   PATCH /api/admin/feedback/:id   body: { status: 'new'|'reviewed'|'in_progress'|'done' }
//
// Submitters land via /api/portal/feedback (retailer Settings form + customer
// portal footer link). Status workflow: new → reviewed → in_progress → done.
// `type` field captures bug/feature/general intent.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

type Portal = "retailer" | "customer" | "supplier";
type FeedbackType = "bug" | "feature" | "general";
type Status = "new" | "reviewed" | "in_progress" | "done" | "replied";

interface WireFeedback {
  id: string;
  portal: string;
  type: string;
  title: string | null;
  description: string;
  screenshotUrl: string | null;
  submitterName: string | null;
  submitterEmail: string | null;
  retailerEmail?: string | null;
  customerEmail?: string | null;
  status: string;
  retailerId: string | null;
  customerId: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string | null;
  updatedAt: string | null;
  retailerName: string | null;
  customerName: string | null;
}

interface Feedback {
  id: string;
  portal: Portal;
  type: FeedbackType;
  title: string;
  description: string;
  screenshotUrl: string | null;
  from: string;
  business: string;
  email: string;
  status: Status;
  whenMs: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function coercePortal(raw: unknown): Portal {
  const v = String(raw ?? "").toLowerCase().trim();
  if (v === "retailer" || v === "customer" || v === "supplier") return v;
  return "retailer";
}

function coerceType(raw: unknown): FeedbackType {
  const v = String(raw ?? "").toLowerCase().trim();
  if (v === "bug" || v === "feature" || v === "general") return v;
  return "general";
}

function coerceStatus(raw: unknown): Status {
  const v = String(raw ?? "").toLowerCase().trim();
  if (v === "new" || v === "reviewed" || v === "in_progress" || v === "done" || v === "replied") return v;
  return "new";
}

function coerceTime(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw < 1e12 ? raw * 1000 : raw;
  }
  if (typeof raw === "string" && raw.trim()) {
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return t;
  }
  return Date.now();
}

function normalise(f: WireFeedback): Feedback {
  const portal = coercePortal(f.portal);
  const fromName = f.submitterName ?? f.retailerName ?? f.customerName ?? f.submitterEmail ?? "Unknown";
  const business = f.retailerName ?? (portal === "customer" ? "Customer" : "");
  const email = f.submitterEmail ?? f.retailerEmail ?? f.customerEmail ?? "";
  return {
    id: String(f.id ?? ""),
    portal,
    type: coerceType(f.type),
    title: String(f.title ?? "").trim() || "(no title)",
    description: String(f.description ?? "").trim(),
    screenshotUrl: f.screenshotUrl,
    from: String(fromName).trim(),
    business: String(business).trim(),
    email: String(email).trim(),
    status: coerceStatus(f.status),
    whenMs: coerceTime(f.createdAt),
  };
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

const dateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

const TYPE_PILL: Record<FeedbackType, string> = {
  bug: "adm-pill-danger",
  feature: "adm-pill-info",
  general: "adm-pill",
};

const TYPE_LABEL: Record<FeedbackType, string> = {
  bug: "Bug",
  feature: "Feature",
  general: "General",
};

const STATUS_PILL: Record<Status, string> = {
  new: "adm-pill-warn",
  reviewed: "adm-pill-info",
  in_progress: "adm-pill-info",
  done: "adm-pill-ok",
  replied: "adm-pill-ok",
};

const STATUS_LABEL: Record<Status, string> = {
  new: "New",
  reviewed: "Reviewed",
  in_progress: "In progress",
  done: "Done",
  replied: "Replied",
};

// ── Page ─────────────────────────────────────────────────────────────────────

const feedbackPage: Page = {
  name: "feedback",
  label: "Feedback",
  icon: "💬",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let items: Feedback[] = [];
    let loading = true;
    let error: string | null = null;
    let backendMissing = false;
    let portalFilter: Portal | "all" = "all";
    let statusFilter: Status | "all" = "all";
    let fromDate = "";
    let toDate = "";

    const root = document.createElement("section");
    root.className = "afb-page";
    root.innerHTML = `
      <header class="afb-header">
        <h1>User Feedback</h1>
        <p>Bug reports, feature requests and general feedback aggregated from every portal.</p>
      </header>

      <div class="afb-toolbar">
        <select class="adm-select afb-filter" data-role="portal" aria-label="Filter by portal">
          <option value="all">All portals</option>
          <option value="retailer">Retailer</option>
          <option value="customer">Customer</option>
          <option value="supplier">Supplier</option>
        </select>
        <select class="adm-select afb-filter" data-role="status" aria-label="Filter by status">
          <option value="all">All statuses</option>
          <option value="new">New</option>
          <option value="reviewed">Reviewed</option>
          <option value="in_progress">In progress</option>
          <option value="done">Done</option>
          <option value="replied">Replied</option>
        </select>
        <label class="afb-date">
          <span>From</span>
          <input type="date" class="adm-input" data-role="from" />
        </label>
        <label class="afb-date">
          <span>To</span>
          <input type="date" class="adm-input" data-role="to" />
        </label>
        <span class="afb-spacer"></span>
        <span class="afb-count" data-role="count"></span>
      </div>

      <div class="adm-card afb-table-wrap" data-role="table-wrap"></div>

      <dialog class="adm-dialog afb-reply-dialog" data-role="reply-dialog">
        <form method="dialog" class="afb-reply-form" data-role="reply-form">
          <h2 class="afb-reply-title">Reply to feedback</h2>
          <p class="afb-reply-meta" data-role="reply-meta"></p>
          <div class="afb-reply-quote" data-role="reply-quote"></div>
          <label class="afb-reply-label" for="afb-reply-body">Your reply</label>
          <textarea id="afb-reply-body" class="adm-input afb-reply-textarea" data-role="reply-body"
                    rows="7" maxlength="8000"
                    placeholder="Write your reply. This is emailed to the submitter."></textarea>
          <p class="afb-reply-error" data-role="reply-error" hidden></p>
          <div class="afb-reply-actions">
            <button type="button" class="adm-btn adm-btn-ghost" data-role="reply-cancel">Cancel</button>
            <button type="button" class="adm-btn adm-btn-primary" data-role="reply-send">Send reply</button>
          </div>
        </form>
      </dialog>
    `;
    container.appendChild(root);

    const portalEl = root.querySelector<HTMLSelectElement>("[data-role='portal']")!;
    const statusEl = root.querySelector<HTMLSelectElement>("[data-role='status']")!;
    const fromEl   = root.querySelector<HTMLInputElement>("[data-role='from']")!;
    const toEl     = root.querySelector<HTMLInputElement>("[data-role='to']")!;
    const countEl  = root.querySelector<HTMLSpanElement>("[data-role='count']")!;
    const tableWrap = root.querySelector<HTMLDivElement>("[data-role='table-wrap']")!;

    const replyDialog = root.querySelector<HTMLDialogElement>("[data-role='reply-dialog']")!;
    const replyMeta   = root.querySelector<HTMLParagraphElement>("[data-role='reply-meta']")!;
    const replyQuote  = root.querySelector<HTMLDivElement>("[data-role='reply-quote']")!;
    const replyBody   = root.querySelector<HTMLTextAreaElement>("[data-role='reply-body']")!;
    const replyError  = root.querySelector<HTMLParagraphElement>("[data-role='reply-error']")!;
    const replyCancel = root.querySelector<HTMLButtonElement>("[data-role='reply-cancel']")!;
    const replySend   = root.querySelector<HTMLButtonElement>("[data-role='reply-send']")!;
    let replyTargetId: string | null = null;
    let replying = false;

    function openReply(item: Feedback): void {
      if (!item.email) return;
      replyTargetId = item.id;
      replyBody.value = "";
      replyError.hidden = true;
      replyError.textContent = "";
      replySend.disabled = false;
      replySend.textContent = "Send reply";
      replyMeta.textContent = `To ${item.from}${item.business ? ` · ${item.business}` : ""} · ${item.email}`;
      replyQuote.textContent = `“${item.title}” — ${item.description}`;
      replyDialog.showModal();
      window.setTimeout(() => replyBody.focus(), 40);
    }

    async function sendReply(): Promise<void> {
      if (replying || !replyTargetId) return;
      const body = replyBody.value.trim();
      if (!body) {
        replyError.hidden = false;
        replyError.textContent = "Write a reply before sending.";
        replyBody.focus();
        return;
      }
      replying = true;
      replySend.disabled = true;
      replySend.textContent = "Sending…";
      replyError.hidden = true;
      const id = replyTargetId;
      try {
        const updated = await ctx.api<{ ok?: boolean; feedback?: WireFeedback } | WireFeedback>(
          `/admin/feedback/${encodeURIComponent(id)}/reply`,
          { method: "POST", body: JSON.stringify({ body }), signal: ac.signal },
        );
        const wire = updated && "feedback" in (updated as Record<string, unknown>)
          ? (updated as { feedback: WireFeedback }).feedback
          : (updated as WireFeedback);
        // The reply endpoint's RETURNING row doesn't re-join retailer/customer
        // email — merge only the status so the row keeps its business/contact.
        const nextStatus = wire && wire.status ? coerceStatus(wire.status) : ("replied" as Status);
        items = items.map((x) => (x.id === id ? { ...x, status: nextStatus } : x));
        render();
        replyDialog.close();
        ctx.showToast("Reply sent", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        const msg =
          err instanceof ApiError && err.status === 422
            ? "No email address on file for this submitter."
            : err instanceof ApiError && err.status === 502
              ? "Email service failed to send. Try again shortly."
              : err instanceof ApiError
                ? `Couldn't send reply (${err.status}).`
                : "Couldn't send reply.";
        replyError.hidden = false;
        replyError.textContent = msg;
      } finally {
        replying = false;
        replySend.disabled = false;
        replySend.textContent = "Send reply";
      }
    }

    function render(): void {
      if (loading) {
        tableWrap.innerHTML = `<div class="adm-loading">Loading feedback…</div>`;
        countEl.textContent = "";
        return;
      }
      if (error) {
        tableWrap.innerHTML = `<div class="adm-error">${esc(error)}</div>`;
        return;
      }
      const visible = items.slice().sort((a, b) => b.whenMs - a.whenMs);
      countEl.textContent = `${visible.length} ${visible.length === 1 ? "item" : "items"}`;
      if (visible.length === 0) {
        tableWrap.innerHTML = backendMissing
          ? `<div class="adm-empty">No feedback yet. (Backend <code>/api/admin/feedback</code> not reachable.)</div>`
          : `<div class="adm-empty">No feedback matches these filters.</div>`;
        return;
      }
      tableWrap.innerHTML = `
        <table class="adm-table afb-table">
          <thead>
            <tr>
              <th>From</th>
              <th>Business</th>
              <th>Contact</th>
              <th>Portal</th>
              <th>Type</th>
              <th>Title</th>
              <th>Message</th>
              <th>When</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            ${visible
              .map(
                (i) => `
              <tr data-id="${esc(i.id)}">
                <td>${esc(i.from)}</td>
                <td>${i.business ? esc(i.business) : `<span class="afb-dim">—</span>`}</td>
                <td>${i.email ? `<a class="afb-contact" href="mailto:${esc(i.email)}">${esc(i.email)}</a>` : `<span class="afb-dim">—</span>`}</td>
                <td><span class="adm-pill">${esc(i.portal)}</span></td>
                <td><span class="adm-pill ${TYPE_PILL[i.type]}">${esc(TYPE_LABEL[i.type])}</span></td>
                <td>${esc(i.title)}</td>
                <td class="afb-msg">${esc(i.description)}${i.screenshotUrl ? ` <a class="afb-shot-link" href="${esc(i.screenshotUrl)}" target="_blank" rel="noopener">[screenshot]</a>` : ""}</td>
                <td>${esc(dateFmt.format(i.whenMs))}</td>
                <td><span class="adm-pill ${STATUS_PILL[i.status]}">${esc(STATUS_LABEL[i.status])}</span></td>
                <td class="afb-actions">
                  <button class="adm-btn adm-btn-ghost afb-btn" type="button" data-role="reply"${i.email ? "" : " disabled title=\"No email on file for this submitter\""}>${i.status === "replied" ? "Reply again" : "Reply"}</button>
                  ${
                    i.status === "new"
                      ? `<button class="adm-btn adm-btn-ghost afb-btn" type="button" data-role="reviewed">Mark reviewed</button>`
                      : i.status === "reviewed"
                        ? `<button class="adm-btn adm-btn-ghost afb-btn" type="button" data-role="in_progress">In progress</button>`
                        : i.status === "in_progress"
                          ? `<button class="adm-btn adm-btn-ghost afb-btn" type="button" data-role="done">Done</button>`
                          : `<button class="adm-btn adm-btn-ghost afb-btn" type="button" data-role="new">Reopen</button>`
                  }
                </td>
              </tr>`,
              )
              .join("")}
          </tbody>
        </table>
      `;
    }

    function buildQuery(): string {
      const qs = new URLSearchParams();
      if (portalFilter !== "all") qs.set("portal", portalFilter);
      if (statusFilter !== "all") qs.set("status", statusFilter);
      if (fromDate) qs.set("from", fromDate);
      if (toDate)   qs.set("to", toDate);
      const s = qs.toString();
      return s ? `?${s}` : "";
    }

    async function load(): Promise<void> {
      loading = true;
      error = null;
      backendMissing = false;
      render();
      try {
        const resp = await ctx.api<{ ok?: boolean; feedback?: WireFeedback[] } | WireFeedback[]>(
          `/admin/feedback${buildQuery()}`,
          { signal: ac.signal },
        );
        // Tolerate both envelope and bare-array responses.
        const list = Array.isArray(resp) ? resp : (resp?.feedback ?? []);
        items = list.map(normalise);
        loading = false;
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        loading = false;
        if (err instanceof ApiError && err.status === 404) {
          backendMissing = true;
          items = [];
          render();
          return;
        }
        error =
          err instanceof ApiError
            ? `Couldn't load feedback (${err.status}).`
            : "Couldn't load feedback.";
        render();
      }
    }

    async function patchStatus(id: string, status: Status): Promise<void> {
      try {
        const updated = await ctx.api<{ ok?: boolean; feedback?: WireFeedback } | WireFeedback>(
          `/admin/feedback/${encodeURIComponent(id)}`,
          {
            method: "PATCH",
            body: JSON.stringify({ status }),
            signal: ac.signal,
          },
        );
        const wire = "feedback" in (updated as Record<string, unknown>)
          ? (updated as { feedback: WireFeedback }).feedback
          : (updated as WireFeedback);
        const n = normalise(wire);
        items = items.map((x) => (x.id === id ? n : x));
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        items = items.map((x) => (x.id === id ? { ...x, status } : x));
        render();
        ctx.showToast("Updated locally (backend unreachable)", "info");
      }
    }

    const onPortalChange = (): void => {
      portalFilter = (portalEl.value as Portal | "all") || "all";
      void load();
    };
    const onStatusChange = (): void => {
      statusFilter = (statusEl.value as Status | "all") || "all";
      void load();
    };
    const onFromChange = (): void => {
      fromDate = fromEl.value || "";
      void load();
    };
    const onToChange = (): void => {
      toDate = toEl.value || "";
      void load();
    };

    const onTableClick = (e: Event): void => {
      const t = e.target as HTMLElement;
      const tr = t.closest<HTMLTableRowElement>("tr[data-id]");
      const id = tr?.dataset.id;
      if (!id) return;
      if (t.matches("[data-role='reply']")) {
        const item = items.find((x) => x.id === id);
        if (item) openReply(item);
        return;
      }
      for (const s of ["new", "reviewed", "in_progress", "done"] as Status[]) {
        if (t.matches(`[data-role='${s}']`)) {
          void patchStatus(id, s);
          return;
        }
      }
    };

    const onReplySend = (): void => { void sendReply(); };
    const onReplyCancel = (): void => { replyDialog.close(); };

    portalEl.addEventListener("change", onPortalChange);
    statusEl.addEventListener("change", onStatusChange);
    fromEl.addEventListener("change", onFromChange);
    toEl.addEventListener("change", onToChange);
    tableWrap.addEventListener("click", onTableClick);
    replySend.addEventListener("click", onReplySend);
    replyCancel.addEventListener("click", onReplyCancel);

    void load();

    return () => {
      ac.abort();
      portalEl.removeEventListener("change", onPortalChange);
      statusEl.removeEventListener("change", onStatusChange);
      fromEl.removeEventListener("change", onFromChange);
      toEl.removeEventListener("change", onToChange);
      tableWrap.removeEventListener("click", onTableClick);
      replySend.removeEventListener("click", onReplySend);
      replyCancel.removeEventListener("click", onReplyCancel);
      if (replyDialog.open) replyDialog.close();
      root.remove();
    };
  },
};

registerPage(feedbackPage);
export default feedbackPage;
